import { SurveyV2, UserDto } from '@act/shared/data-transfer-objects';
import { Box, Step, StepContent, Stepper, Button } from '@mui/material';
import { useState, useMemo } from 'react';
import StepIconLabel from 'react-components/surveys/StepIconLabel';
import SurveyTitleSection from 'react-components/surveys/SurveyTitleSection';
import QuestionMock from './QuestionMock';
import { applyMustacheTemplate } from '@act/shared/utilities';

export interface SurveyStepperPreviewProps {
  content: SurveyV2.CreateSurveyJSONDto;
  member: Partial<UserDto>;
}

const SurveyStepperLivePreview = ({
  content,
  member,
}: SurveyStepperPreviewProps) => {
  const questions = useMemo(
    () => content?.surveyContent?.surveyVersion?.questions || [],
    [content?.surveyContent?.surveyVersion?.questions]
  );

  const [answers, setAnswers] = useState<Record<string, string | string[]>>({});
  const [selectedStep, setSelectedStep] = useState(0);
  const [completedCheckpoints, setCompletedCheckpoints] = useState<string[]>(
    []
  );

  const steps = useMemo(() => {
    const visibleSteps: (
      | SurveyV2.CreateQuestionJSONDto
      | SurveyV2.CreateCheckpointJSONDto
    )[] = [];

    // If there are no questions, return an empty array
    if (questions.length === 0) return visibleSteps;

    // Add the first question to the visible steps
    let currentQuestion = questions[0];
    visibleSteps.push(currentQuestion);

    /**
     * Loop through the questions, and their options, and add the next question
     * or checkpoint to the visible steps if they should be visible. This is based
     * on whether the question has been answered and is required or optional otherwise.
     * Then if the question or one of its options has a checkpoint, add it to the visible
     * steps if the checkpoint has not been completed-which in this in this context is
     * based on clicking next when viewing a checkpoint. Option checkpoints will
     * supercede question checkpoints.
     */
    while (currentQuestion) {
      let nextQuestion = null;
      let checkpointToAdd = null;

      // Check for option-based checkpoint and next question
      if (currentQuestion.options && answers[currentQuestion.key]) {
        const answer = answers[currentQuestion.key];
        const selectedOption = currentQuestion.options.find(
          opt => String(answer) === String(opt.sortOrder)
        );

        // If the question has a checkpoint, add it to the visible steps if the checkpoint has not been completed
        if (selectedOption?.checkpoint) {
          checkpointToAdd = selectedOption.checkpoint;
        }

        /**
         * If the question has a next question and there is not a
         * completion checkpoint add it to the visible steps
         */
        if (
          !checkpointToAdd?.isCompletionCheckpoint &&
          selectedOption?.nextQuestionKey
        ) {
          nextQuestion = questions.find(
            q => q.key === selectedOption.nextQuestionKey
          );
        }
      }

      // If the question has a checkpoint and there is not a completion checkpoint add it to the visible steps
      if (!checkpointToAdd && currentQuestion.checkpoint) {
        checkpointToAdd = currentQuestion.checkpoint;
      }

      // Only look for next question if we don't have a completion checkpoint
      if (!checkpointToAdd?.isCompletionCheckpoint) {
        if (!nextQuestion && currentQuestion.nextQuestionKey) {
          nextQuestion = questions.find(
            q => q.key === currentQuestion.nextQuestionKey
          );
        }

        // If there is not a next question, find the next question in the list
        if (!nextQuestion) {
          nextQuestion = questions.find(
            q => q.sortOrder > currentQuestion.sortOrder
          );
        }
      }

      // If we have an answer and found a checkpoint
      if (answers[currentQuestion.key] && checkpointToAdd) {
        // Only show checkpoint if it hasn't been completed
        if (!completedCheckpoints.includes(checkpointToAdd.title)) {
          visibleSteps.push(checkpointToAdd);
          // Break if it's a completion checkpoint
          if (checkpointToAdd.isCompletionCheckpoint) {
            break;
          }
        }
      }

      // Add next question if appropriate and we don't have a completion checkpoint
      if (
        answers[currentQuestion.key] &&
        nextQuestion &&
        !checkpointToAdd?.isCompletionCheckpoint
      ) {
        visibleSteps.push(nextQuestion);
        currentQuestion = nextQuestion;
      } else {
        break;
      }
    }

    return visibleSteps;
  }, [questions, answers, selectedStep, completedCheckpoints]);

  const activeStep = selectedStep === null ? steps.length - 1 : selectedStep;

  const handleStep = (step: number): void => {
    setSelectedStep(step);
  };

  const handleChange = (questionKey: string, answer: string | string[]) => {
    if (answer) {
      setAnswers(prev => ({ ...prev, [questionKey]: answer }));
    }
  };

  const handleCheckpointComplete = (checkpointTitle: string) => {
    setCompletedCheckpoints(prev => [...prev, checkpointTitle]);
    setSelectedStep(prev => prev); // Trigger a re-render
  };

  return (
    <Box>
      <SurveyTitleSection
        survey={content.surveyContent as unknown as SurveyV2.SurveyDto}
        member={member as UserDto}
      />
      <Stepper nonLinear activeStep={activeStep} orientation="vertical">
        {steps.map((step, index) => {
          if (Object.hasOwn(step, 'key')) {
            const questionStep = step as SurveyV2.CreateQuestionJSONDto;

            if (questionStep.title === '' && questionStep.text === '') {
              return null;
            }

            return (
              <Step key={questionStep.key}>
                <StepIconLabel
                  title={questionStep.title}
                  isActive={activeStep === index}
                  handleStep={() => handleStep(index)}
                />
                <StepContent sx={{ paddingTop: '24px' }}>
                  <QuestionMock
                    question={questionStep}
                    member={member as UserDto}
                    activeStep={activeStep}
                    numberOfSteps={steps.length}
                    handleChange={handleChange}
                    handleStep={handleStep}
                  />
                </StepContent>
              </Step>
            );
          }

          const checkpointStep = step as SurveyV2.CreateCheckpointJSONDto;

          return (
            <Step key={checkpointStep.title}>
              <StepIconLabel
                title={checkpointStep.title}
                isActive={activeStep === index}
                handleStep={() => handleStep(index)}
              />
              <StepContent sx={{ paddingTop: '24px' }}>
                <Box>
                  {applyMustacheTemplate(checkpointStep.message, {
                    memberName: `${member?.firstName}`,
                  })}
                  {!checkpointStep.isCompletionCheckpoint && (
                    <Box
                      sx={{
                        mt: 2,
                        mb: 1,
                        display: 'flex',
                        justifyContent: 'flex-end',
                      }}
                    >
                      <Button
                        variant="contained"
                        onClick={() =>
                          handleCheckpointComplete(checkpointStep.title)
                        }
                      >
                        Next
                      </Button>
                    </Box>
                  )}
                </Box>
              </StepContent>
            </Step>
          );
        })}
      </Stepper>
    </Box>
  );
};

export default SurveyStepperLivePreview;
